import { Button, Select } from '@ludus/ui'
import { useTranslation } from 'react-i18next'
import { useStageState } from '../useStageState.ts'

const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'] as const

interface PaginationBarProps {
  /** Full filtered count across all pages — drives the "X–Y of N" summary + nav. */
  total: number
}

/**
 * Pinned footer of the leads panel: page-size + pagination, always at the bottom
 * of the visible box (rendered as a sibling *below* the scroll area, not inside
 * it — so the list scrolls above it, the last row is never occluded, and it spans
 * the full panel width clear of the list's scrollbar). The panel's
 * `overflow-hidden rounded-xl` rounds its bottom corners.
 */
export function PaginationBar({ total }: PaginationBarProps) {
  const { t } = useTranslation()
  const page = useStageState((s) => s.page)
  const setPage = useStageState((s) => s.setPage)
  const pageSize = useStageState((s) => s.pageSize)
  const setPageSize = useStageState((s) => s.setPageSize)

  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const safePage = Math.min(page, totalPages - 1)

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-border/60 bg-background-secondary/40 px-3 py-2 text-xs text-muted">
      <span>
        {t('pages.pipelines.pagination.summary', {
          defaultValue: '{{from}}–{{to}} of {{total}}',
          from: total === 0 ? 0 : safePage * pageSize + 1,
          to: Math.min((safePage + 1) * pageSize, total),
          total,
        })}
      </span>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2">
          {/* Label hidden on mobile to keep the bar to one tight row. */}
          <span className="hidden whitespace-nowrap sm:inline">
            {t('pages.pipelines.pagination.perPage', { defaultValue: 'Per page' })}
          </span>
          <Select<string>
            value={String(pageSize)}
            options={PAGE_SIZE_OPTIONS.map((v) => ({ value: v, label: v }))}
            onValueChange={(v) => setPageSize(Number(v))}
            className="min-w-[72px]"
          />
        </label>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={safePage === 0}
            onClick={() => setPage(safePage - 1)}
            aria-label="Previous page"
          >
            ←
          </Button>
          <span className="px-2 tabular-nums">
            {safePage + 1} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage(safePage + 1)}
            aria-label="Next page"
          >
            →
          </Button>
        </div>
      </div>
    </div>
  )
}
